import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ExternalLink } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { Slot } from "../../primitives/Slot.js";

/* `default` and `muted` carry the underline (at rest, decoration colour
   shifts on hover); `entity` and `plain` do not, so they own no underline
   classes of the base. `display` owns the box model: `inline-flex` (default)
   is today's icon-row layout, `inline` lets long text wrap inside prose, and
   `block` fills its container. `stretched` adds the transparent `::after`
   overlay that expands the link's hit area to the nearest positioned
   ancestor (a Card, a row) — the caller lifts any sibling control that must
   stay clickable with `relative z-10` above it. */
export const linkVariants = cva(
  "rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal active:decoration-pb-text",
  {
    variants: {
      variant: {
        default:
          "underline underline-offset-2 decoration-pb-border-strong text-pb-accent-text hover:text-pb-accent-active hover:decoration-pb-accent/50",
        muted:
          "underline underline-offset-2 decoration-pb-border-strong text-pb-text-secondary hover:text-pb-text hover:decoration-pb-border-strong",
        /* The LAMA_ENTITY_LINK_CLASS look: an entity reference in a table
           cell or list row. Underlined only on hover, so a dense list of
           entity links reads as text, not a wall of underscores. */
        entity: "font-medium text-pb-accent-text hover:underline",
        /* Inherits the surrounding colour and is never underlined — the same
           meaning `plain` has on Button. For a link the caller wants to read
           as part of its sentence or row, not as a link. */
        plain: "text-current",
      },
      display: {
        "inline-flex": "inline-flex items-center gap-1",
        inline: "break-words [overflow-wrap:anywhere]",
        block: "block",
      },
      stretched: {
        true: "after:absolute after:inset-0 after:content-['']",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      display: "inline-flex",
      stretched: false,
    },
  },
);

interface LinkBaseProps
  extends
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {}

export type LinkProps = LinkBaseProps &
  (
    | {
        /** Marks an external destination: adds the icon + safe rel, opens a new tab. */
        external?: boolean;
        /** Default `false`: the Link renders its own <a>. */
        asChild?: false | undefined;
      }
    | {
        /** Paints the one child element (the host router's link) with the
         *  link classes and renders no <a> of its own (primitives/Slot.tsx).
         *  Not with `external`: a router link is in-app. */
        asChild: true;
        external?: never;
      }
  );

/* Anchor-semantics link (guide §10 "Link", register #12). Router-agnostic by
   design: packages/ui has no router. For in-app navigation, wrap the host
   router's own <Link> in `asChild` (link-button-aschild): one <a>, the
   router's href and click, this Link's classes. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    variant,
    display,
    stretched,
    external,
    asChild = false,
    className,
    children,
    target,
    rel,
    ...rest
  },
  ref,
) {
  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={cn(linkVariants({ variant, display, stretched }), className)}
        target={target}
        rel={rel}
        {...rest}
      >
        {children}
      </Slot>
    );
  }
  // External links always carry the safety tokens — merged with (never
  // replaced by) any caller-supplied rel, so target="_blank" stays safe.
  const externalRel = [
    ...new Set(["noopener", "noreferrer", ...(rel?.split(/\s+/) ?? [])]),
  ].join(" ");
  const externalProps = external
    ? { target: target ?? "_blank", rel: externalRel }
    : { target, rel };
  return (
    <a
      ref={ref}
      className={cn(linkVariants({ variant, display, stretched }), className)}
      {...externalProps}
      {...rest}
    >
      {children}
      {external && (
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      )}
    </a>
  );
});
